import { api } from "./client";
import type { AttachmentRef, PresignRequest, PresignResponse } from "@/types/api";

export async function presign(payload: PresignRequest): Promise<PresignResponse> {
  const { data } = await api.post<PresignResponse>("/uploads/presign", payload);
  return data;
}

export interface UploadOpts {
  onProgress?: (pct: number) => void;
  signal?: AbortSignal;
}

export async function uploadImage(
  conversationId: number,
  file: File,
  opts: UploadOpts = {}
): Promise<AttachmentRef> {
  const presigned = await presign({
    conversation_id: conversationId,
    filename: file.name,
    content_type: file.type,
  });

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presigned.url);
    xhr.setRequestHeader("Content-Type", file.type);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && opts.onProgress) {
        opts.onProgress((e.loaded / e.total) * 100);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        opts.onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`S3 PUT failed: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.onabort = () => reject(new DOMException("Upload aborted", "AbortError"));

    if (opts.signal) {
      if (opts.signal.aborted) {
        xhr.abort();
      } else {
        opts.signal.addEventListener("abort", () => xhr.abort(), { once: true });
      }
    }

    xhr.send(file);
  });

  return {
    s3_key: presigned.s3_key,
    mime: file.type,
    filename: file.name,
    byte_size: file.size,
    url: presigned.get_url,
  };
}
